import { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";

const WerkplaatsContactSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    licensePlate: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const handleScroll = useCallback(() => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const triggerStart = viewportHeight * 0.8;
    const triggerEnd = viewportHeight * 0.1;
    const currentTop = rect.top;

    if (currentTop < triggerStart && currentTop > -viewportHeight) {
      const progress = Math.min(1, Math.max(0, (triggerStart - currentTop) / (triggerStart - triggerEnd)));
      setScrollProgress(progress);
    } else if (currentTop >= triggerStart) {
      setScrollProgress(0);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.phone.trim()) {
      toast({
        variant: "destructive",
        title: "Velden incompleet",
        description: "Vul minimaal uw naam en telefoonnummer in.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const supabaseUrl = 'https://lozejxkhsvwkksgzhrfd.supabase.co';
      const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxvemVqeGtoc3Z3a2tzZ3pocmZkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0OTY4OTcsImV4cCI6MjA4MjA3Mjg5N30.GvcNibeMI-dF1dq4s4mq5Ap9Edspot9s5mdjUEWfZ0M';
      const functionUrl = `${supabaseUrl}/functions/v1/send-contact`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          licensePlate: formData.licensePlate.trim(),
          message: formData.message.trim()
        }),
      });

      const responseText = await response.text();
      let result;
      if (responseText.trim()) {
        try {
          result = JSON.parse(responseText);
        } catch {
          throw new Error('Invalid server response format');
        }
      } else {
        throw new Error('Empty server response');
      }

      if (!response.ok || !result.success) {
        throw new Error(result.error || `Server error: ${response.status}`);
      }

      setIsSuccess(true);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Er ging iets mis",
        description: error instanceof Error ? error.message : "Probeer het opnieuw of neem direct contact met ons op.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const maxPadding = 24;
  const currentPadding = maxPadding * scrollProgress;
  const currentRadius = 24 * scrollProgress;

  return (
    <section ref={sectionRef} className="bg-white pb-20 md:pb-28" style={{ paddingTop: `${Math.max(0, 8 * scrollProgress)}px` }}>
      <div
        className="relative w-full mx-auto overflow-hidden transition-[border-radius] duration-100 bg-black"
        style={{
          marginLeft: `${currentPadding}px`,
          marginRight: `${currentPadding}px`,
          width: `calc(100% - ${currentPadding * 2}px)`,
          borderRadius: `${currentRadius}px`,
        }}
      >
        <div className="py-20 md:py-28 lg:py-32">
          <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              {/* Left: image */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="rounded-2xl overflow-hidden aspect-[4/3]"
              >
                <img
                  src="https://lozejxkhsvwkksgzhrfd.supabase.co/storage/v1/object/public/images/Vermado%20automotive_Website%20fotos%20%2010%20april-25.jpg"
                  alt="Vermado werkplaats"
                  className="w-full h-full object-cover"
                />
              </motion.div>

              {/* Right: tagline + headline + form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <p
                  className="text-white/50 text-xs tracking-[0.15em] uppercase mb-6"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Professionele service <span className="bg-[#B1A06D] text-black px-1.5 py-0.5 rounded text-[10px] ml-1 font-semibold">VERMADO</span>
                </p>

                <h2
                  className="text-white text-3xl md:text-4xl lg:text-[2.75rem] font-medium leading-[1.15] mb-6 uppercase tracking-[0.04em]"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Neem contact op
                </h2>

                <p
                  className="text-white/70 text-sm leading-[1.75] mb-10 max-w-md"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Laat uw gegevens achter en wij nemen zo snel mogelijk contact met u op.
                </p>

                {isSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white/5 border border-white/10 rounded-xl p-8 text-center"
                  >
                    <div className="w-14 h-14 bg-[#B1A06D] rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-7 h-7 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-white text-xl font-semibold mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>Bericht verzonden!</h3>
                    <p className="text-white/60 text-sm mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
                      We nemen zo snel mogelijk contact met u op.
                    </p>
                    <button
                      onClick={() => { setIsSuccess(false); setFormData({ name: "", phone: "", email: "", licensePlate: "", message: "" }); }}
                      className="text-[#B1A06D] text-sm underline underline-offset-4 hover:text-[#B1A06D]/80 transition-colors"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      Nieuw bericht
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        name="name"
                        type="text"
                        placeholder="Uw naam"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="bg-white border-0 text-black placeholder:text-black/50 focus:ring-[#B1A06D] h-12 rounded-xl"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                        required
                      />
                      <Input
                        name="phone"
                        type="tel"
                        placeholder="Telefoonnummer"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="bg-white border-0 text-black placeholder:text-black/50 focus:ring-[#B1A06D] h-12 rounded-xl"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        name="email"
                        type="email"
                        placeholder="E-mailadres"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="bg-white border-0 text-black placeholder:text-black/50 focus:ring-[#B1A06D] h-12 rounded-xl"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      />
                      <Input
                        name="licensePlate"
                        type="text"
                        placeholder="Kenteken"
                        value={formData.licensePlate}
                        onChange={handleInputChange}
                        className="bg-white border-0 text-black placeholder:text-black/50 focus:ring-[#B1A06D] h-12 rounded-xl uppercase"
                        style={{ fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase' }}
                      />
                    </div>
                    <Textarea
                      name="message"
                      placeholder="Bericht"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="bg-white border-0 text-black placeholder:text-black/50 focus:ring-[#B1A06D] rounded-xl resize-none"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="mt-2 bg-white text-black text-xs tracking-[0.1em] uppercase px-8 py-4 rounded-full flex items-center gap-2 hover:bg-white/90 transition-colors font-medium disabled:opacity-50"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {isSubmitting ? 'Bezig met verzenden...' : 'Verstuur bericht'}
                    </button>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WerkplaatsContactSection;
